import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";

import { AddPatrons } from "@/features/AddPatrons";
import {
  AddPeopleSearch_Atom,
  currentAddPatronsSlotIdAtom,
} from "@/features/AddPatrons/atoms/stores";
import { Patron } from "@/features/AddPatrons/types";
import { addKnownDiningPersonAtom } from "@/features/BookingWizard/DiningBookingWizard/atoms/actions/addKnownPerson/addKnownPerson";
import { removeKnownDiningPersonAtom } from "@/features/BookingWizard/DiningBookingWizard/atoms/actions/removeKnownPerson/removeKnownPerson";
import { isGroupFullOfKnownPeopleAtom } from "@/features/BookingWizard/DiningBookingWizard/atoms/selectors/isGroupFullOfKnownPeople/isGroupFullOfKnownPeople";
import {
  DiningBookingKnownPeopleAtom,
  DiningBookingOwnerMemberAtom,
} from "@/features/BookingWizard/DiningBookingWizard/atoms/stores";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { DiningBookingKnownPeopleDisplay } from "./components/DiningBookingKnownPeopleDisplay/DiningBookingKnownPeopleDisplay";

type Props = {
  startSlotId: string | null;
};

export const DiningAddKnownPeopleStep = ({ startSlotId }: Props) => {
  const [search, setSearch] = useAtom(AddPeopleSearch_Atom);
  const setCurrentSlotId = useSetAtom(currentAddPatronsSlotIdAtom);
  const knownPeople = useAtomValue(DiningBookingKnownPeopleAtom);
  const ownerMember = useAtomValue(DiningBookingOwnerMemberAtom);
  const isGroupFull = useAtomValue(isGroupFullOfKnownPeopleAtom);
  const addKnownPerson = useSetAtom(addKnownDiningPersonAtom);
  const removeKnownPerson = useSetAtom(removeKnownDiningPersonAtom);

  useEffect(() => {
    if (startSlotId) {
      setCurrentSlotId(startSlotId);
    }

    // Clear any prior search value whenever this step mounts
    setSearch("");

    return () => {
      setCurrentSlotId("");
    };
  }, [setCurrentSlotId, setSearch, startSlotId]);

  const currentPatron: Patron | undefined = useMemo(() => {
    if (!ownerMember) {
      return undefined;
    }

    return {
      playerId: ownerMember.playerId,
      firstName: ownerMember.firstName ?? "",
      lastName: ownerMember.lastName ?? "",
      profileImageId: ownerMember.profileImagePublicId ?? undefined,
      type: "Member",
      memberGroups: [],
    };
  }, [ownerMember]);

  const handleMemberToggle = (player: Patron, checked: boolean) => {
    const identifier = player.playerId || player.internalIdentifier || "";
    if (!identifier) return;

    if (checked) {
      if (isGroupFull) return;
      addKnownPerson({
        type: "Member",
        playerId: identifier,
        firstName: player.firstName || "",
        lastName: player.lastName || "",
        profileImagePublicId: player.profileImageId || "",
      });
    } else {
      removeKnownPerson(identifier);
    }
  };

  const handleGuestToggle = (player: Patron, checked: boolean) => {
    const identifier = player.playerId || player.internalIdentifier || "";
    if (!identifier) return;

    if (checked) {
      if (isGroupFull) return;
      addKnownPerson({
        type: "Guest",
        playerId: identifier,
        firstName: player.firstName || "",
        lastName: player.lastName || "",
        profileImagePublicId: player.profileImageId || "",
      });
    } else {
      removeKnownPerson(identifier);
    }
  };

  return (
    <VStack flex={1} bg="surface-secondary">
      <DiningBookingKnownPeopleDisplay />
      <AddPatrons
        search={search}
        setSearch={setSearch}
        currentPatron={currentPatron}
        isGroupFullOfAttendees={false}
        checkNewDisabled={false}
        isGroupFullOfGuests={isGroupFull}
        isGroupFullOfMembers={isGroupFull}
        areGuestsAllowed={true}
        areMembersAllowed={true}
        canAddNewGuest={false}
        memberHoldEnabled={false}
        unknownGuestsEnabled={false}
        showGuestListOverlay={false}
        unknownGuestLimit={0}
        unknownGuestCount={0}
        memberHoldCount={0}
        memberHoldLimit={0}
        handleAddMemberClick={handleMemberToggle}
        handleAddGuestClick={handleGuestToggle}
        players={knownPeople.map((person) => person.playerId)}
        allPlayers={knownPeople.map((person) => person.playerId)}
        bg="surface-primary"
        includeTransportationType={false}
        supportsPublic={false}
        keepItemInListAfterCheck={true}
      />
    </VStack>
  );
};
